// src/oura/onboarding/StepText.tsx
//
// Renders the guides: a numbered list of steps, with the names of things on
// the other product's screen in bold, and the one address every invite goes
// to with a Copy button beside it.

import { useRef, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { FOCUS_RING, SURFACE, TYPE } from "@oura/ui";
import { ACCESS_EMAIL } from "./guides";

/** One step. Words between asterisks render in bold. */
export function StepText({ text }: { text: string }) {
    const parts = text.split("*");
    return (
        <>
            {parts.map((part, i) =>
                i % 2 === 1 ? <strong key={i} className="font-semibold text-gray-900">{part}</strong> : <span key={i}>{part}</span>,
            )}
        </>
    );
}

/** The steps in order. The order is the instruction, so it is a real list. */
export function GuideSteps({ steps, className }: { steps: string[]; className?: string }) {
    return (
        <ol className={cn("list-plain m-0 p-0", className)}>
            {steps.map((step, i) => (
                <li key={step} className={cn("flex gap-3 border-t py-2.5 first:border-t-0", SURFACE.hairline)}>
                    <span aria-hidden className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-gray-100 text-xs font-semibold tabular-nums text-gray-500">
                        {i + 1}
                    </span>
                    <span className={cn(TYPE.tb, "min-w-0 text-gray-700")}>
                        <StepText text={step} />
                    </span>
                </li>
            ))}
        </ol>
    );
}

/** What to do once the address is selected, on the device in hand. */
function howToCopy(): string {
    const touch = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
    return touch ? "Press and hold to copy" : "Press Ctrl or Cmd + C";
}

/** The address every invite goes to. Selectable, with Copy right beside it rather than at the far edge. */
export function AccessAddress({ className }: { className?: string }) {
    const [state, setState] = useState<"idle" | "copied" | "selected">("idle");
    const address = useRef<HTMLSpanElement>(null);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(ACCESS_EMAIL);
            setState("copied");
        } catch {
            // The browser refused. Select the address, so it is 1 step from copied.
            const node = address.current;
            if (node) {
                const range = document.createRange();
                range.selectNodeContents(node);
                const selection = window.getSelection();
                selection?.removeAllRanges();
                selection?.addRange(range);
            }
            setState("selected");
        }
        window.setTimeout(() => setState("idle"), 4000);
    };
    return (
        <div className={cn(SURFACE.inner, "flex w-fit max-w-full items-center gap-3", className)}>
            <span ref={address} className={cn(TYPE.tb, "min-w-0 select-all break-all font-medium text-gray-900")}>{ACCESS_EMAIL}</span>
            <button
                type="button"
                onClick={() => void copy()}
                className={cn(TYPE.tc, "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-white px-3 font-medium text-gray-900 ring-1 ring-inset ring-black/[0.08] hover:bg-gray-50 sm:h-7", FOCUS_RING)}
            >
                {state === "copied" ? <CheckIcon aria-hidden size={14} className="text-emerald-600" /> : <CopyIcon aria-hidden size={14} />}
                <span aria-live="polite">{state === "copied" ? "Copied" : state === "selected" ? howToCopy() : "Copy"}</span>
            </button>
        </div>
    );
}
